/**
 * Certificate Hooks
 * React Query hooks for certificates and identity verification
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUserCertificates,
  checkEligibility,
  requestCertificate,
  verifyCertificate,
  getVerificationStatus,
  submitVerification,
  type SubmitVerificationRequest,
} from "../api/certificatesApi";
import toast from "react-hot-toast";
import { getErrorMessage } from "@/lib/api";

/**
 * Get user's certificates
 */
export function useCertificates() {
  return useQuery({
    queryKey: ["certificates"],
    queryFn: getUserCertificates,
  });
}

/**
 * Check certificate eligibility for a course
 */
export function useCertificateEligibility(courseId: string) {
  return useQuery({
    queryKey: ["certificate-eligibility", courseId],
    queryFn: () => checkEligibility(courseId),
    enabled: !!courseId,
  });
}

/**
 * Request a certificate
 */
export function useRequestCertificate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      courseId,
      recipientName,
    }: {
      courseId: string;
      recipientName?: string;
    }) => requestCertificate(courseId, recipientName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificates"] });
      queryClient.invalidateQueries({ queryKey: ["certificate-eligibility"] });
      toast.success("¡Certificado generado exitosamente!");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Verify a certificate by code
 */
export function useVerifyCertificate(code: string) {
  return useQuery({
    queryKey: ["certificate-verification", code],
    queryFn: () => verifyCertificate(code),
    enabled: !!code && code.length > 5,
  });
}

/**
 * Get identity verification status
 */
export function useVerificationStatus() {
  return useQuery({
    queryKey: ["verification-status"],
    queryFn: getVerificationStatus,
  });
}

/**
 * Submit identity verification
 */
export function useSubmitVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubmitVerificationRequest) => submitVerification(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["verification-status"] });
      toast.success(
        "Verificación enviada. Te notificaremos cuando sea revisada.",
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
